import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

const OverflowAware = forwardRef((props, ref) => {
  const {
    original,
    fallback,
    direction = "vertical",
    className = "",
    containerRef,
  } = props;

  const originalRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [originalSize, setOriginalSize] = useState({
    height: null,
    width: null,
  });

  const manualCheck = () => {
    check();
  };

  // Expose doSomething to the parent
  useImperativeHandle(ref, () => ({
    manualCheck,
  }));

  const check = () => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    let el2 = originalRef.current;
    const hasOverflow =
      direction === "horizontal"
        ? el2.scrollWidth > el.clientWidth
        : el2.scrollHeight > el.clientHeight;
    setOriginalSize({ height: el.clientHeight, width: el.clientWidth });
    setIsOverflowing(hasOverflow);
  };

  useEffect(() => {
    if (!containerRef.current) return;

    const el = containerRef.current;

    // Slight delay to allow layout to settle
    requestAnimationFrame(() => {
      check();
    });

    // Optional: re-check on window resize
    const resizeObserver = new ResizeObserver(() => {
      setIsOverflowing(false); // reset

      check();
    });

    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <>
      {/* Hidden measurement div */}
      <div
        className={`absolute invisible pointer-events-none ${className}`}
        style={{
          position: "absolute",
          visibility: "hidden",
          height: containerRef?.current?.clientHeight ?? 0,
          width: containerRef?.current?.clientWidth ?? 0,
        }}
      >
        <div ref={originalRef}>{original}</div>
      </div>

      {/* Actual visible content */}
      <div className={className}>{isOverflowing ? fallback : original}</div>
    </>
  );
});

export default OverflowAware;
